/**
 * Notiz-Tool — drop a text label anywhere on the surface.
 *
 *   Left-click → window.prompt() for text → place a sprite + small pin.
 *
 * Each note lives in its own Three.js Group so the Ebenen-Panel can
 * toggle / delete it like distance + area measurements.
 */

import {
  Color,
  Object3D,
  Scene as ThreeScene,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector3,
} from 'three'

import { buildMarkerCone } from './markerCone'
import {
  AuthorChip,
  disposeChildren,
  drawAuthorCircle,
  roundedRect,
  LABEL_BASE_HEIGHT_M,
  type MeasurementListener,
} from './DistanceTool'
import { Measurement, nextId } from '../measurements'

const MARKER_RADIUS = 0.09

export class NoteTool {
  private color: number
  private listener: MeasurementListener | null = null
  private serial = 0
  /** Multiplier on label base size (live-adjustable). */
  private labelScale = 1

  constructor(private scene: ThreeScene, color: number = 0xffd400) {
    this.color = color
  }

  setColor(color: number): void {
    this.color = color
  }

  setListener(fn: MeasurementListener | null): void {
    this.listener = fn
  }

  setLabelScale(scale: number): void {
    this.labelScale = scale
    this.scene.traverse((child) => {
      const ud = (child as Sprite).userData as
        | { baseScaleX?: number; baseScaleY?: number; isNoteLabel?: boolean }
        | undefined
      if (
        ud?.isNoteLabel &&
        typeof ud.baseScaleX === 'number' &&
        typeof ud.baseScaleY === 'number'
      ) {
        ;(child as Sprite).scale.set(ud.baseScaleX * scale, ud.baseScaleY * scale, 1)
      }
    })
  }

  /** Drop a note at the picked point.  Prompts for text; empty / cancel → no-op. */
  handleClick(point: Vector3): void {
    const text = window.prompt('Notiz:')?.trim()
    if (!text) return

    const group = new Object3D()
    group.add(buildMarkerCone(point, this.color, MARKER_RADIUS))
    const anchor = point.clone().add(new Vector3(0, 0, MARKER_RADIUS * 3))
    const noteSprite = makeWrappedLabel(text, anchor, this.color, 0x000000)
    noteSprite.userData.isNoteLabel = true
    noteSprite.scale.multiplyScalar(this.labelScale)
    group.add(noteSprite)
    this.scene.add(group)

    this.serial += 1
    const measurement: Measurement = {
      id: nextId(),
      kind: 'note',
      label: `Notiz ${this.serial} — ${text}`,
      visible: true,
      group,
      color: this.color,
    }
    group.name = `note-${measurement.id}`
    group.userData.measurementId = measurement.id
    this.listener?.({ added: measurement })
  }

  removeMeasurement(_id: string, group: Object3D): void {
    this.scene.remove(group)
    disposeChildren(group)
  }

  /** Bump the per-session serial so newly created notes don't reuse
   * "Notiz 1" after restoring existing annotations. */
  setSerial(value: number): void {
    if (value > this.serial) this.serial = value
  }

  /** Build a note group (cone + wrapped sprite) from a saved payload.
   * `point` is the anchor; `text` is the user-supplied body. */
  buildGroupFromPayload(point: Vector3, color: number, text: string): { group: Object3D } {
    const group = new Object3D()
    group.add(buildMarkerCone(point, color, MARKER_RADIUS))
    const anchor = point.clone().add(new Vector3(0, 0, MARKER_RADIUS * 3))
    const noteSprite = makeWrappedLabel(text, anchor, color, 0x000000)
    noteSprite.userData.isNoteLabel = true
    noteSprite.scale.multiplyScalar(this.labelScale)
    group.add(noteSprite)
    this.scene.add(group)
    return { group }
  }

  dispose(): void {
    /* notes are persisted by Scene; nothing per-tool to tear down */
  }
}

// ─── wrapped multi-line label ────────────────────────────────────────────────

/** Greedy word-wrap that also force-breaks tokens longer than maxWidthPx.
 * The first line uses `firstLineMaxWidthPx` if provided (smaller, to leave
 * room for an author chip in the upper-right corner). */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidthPx: number,
  firstLineMaxWidthPx: number = maxWidthPx,
): string[] {
  const out: string[] = []
  const limitFor = (): number => (out.length === 0 ? firstLineMaxWidthPx : maxWidthPx)
  for (const para of text.split(/\r?\n/)) {
    const words = para.length === 0 ? [''] : para.split(/\s+/)
    let line = ''
    for (const wRaw of words) {
      let w = wRaw
      while (w && ctx.measureText(w).width > limitFor()) {
        let cut = 1
        while (cut < w.length && ctx.measureText(w.slice(0, cut + 1)).width <= limitFor()) cut++
        const candidate = line ? `${line} ${w.slice(0, cut)}` : w.slice(0, cut)
        if (ctx.measureText(candidate).width <= limitFor()) {
          line = candidate
        } else {
          if (line) out.push(line)
          line = w.slice(0, cut)
        }
        out.push(line)
        line = ''
        w = w.slice(cut)
      }
      if (!w) continue
      const candidate = line ? `${line} ${w}` : w
      if (ctx.measureText(candidate).width <= limitFor()) {
        line = candidate
      } else {
        if (line) out.push(line)
        line = w
      }
    }
    out.push(line)
  }
  return out
}

/** Multi-line pixel label — same look as makePixelLabel but wraps at ~280 CSS px.
 * Exported so `annotations.ts` can rebuild it with a different chip. */
export function makeWrappedLabel(
  text: string,
  position: Vector3,
  bg: number,
  fg: number,
  chip?: AuthorChip | null,
): Sprite {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const fontSizeCss = 14
  const fontSize = Math.round(fontSizeCss * dpr)
  const paddingCss = 6
  const padding = Math.round(paddingCss * dpr)
  const maxWidthCss = 280
  const maxWidthPx = maxWidthCss * dpr
  const lineHeightCss = fontSizeCss + 4
  const lineHeight = Math.round(lineHeightCss * dpr)

  // Chip is drawn INSIDE the rect in the upper-right corner. Reserve extra
  // room on the right of the first line so the chip never sits on text.
  // Diameter scales with the line height so it visually matches the value
  // labels (≈ 18 px CSS for default 14 px font).
  const chipDiameterCss = chip ? Math.round(lineHeightCss + 4) : 0
  const chipReserveCss = chip ? chipDiameterCss + 6 : 0
  const firstLineMaxWidthPx = maxWidthPx - padding * 2 - chipReserveCss * dpr

  const measure = document.createElement('canvas').getContext('2d')!
  const fontDecl = `bold ${fontSize}px -apple-system, "SF Pro Text", sans-serif`
  measure.font = fontDecl
  const lines = wrapLines(measure, text, maxWidthPx - padding * 2, firstLineMaxWidthPx)

  // Final canvas size based on longest line, plus chip reserve for the first row.
  let widestPx = 0
  for (const ln of lines) widestPx = Math.max(widestPx, measure.measureText(ln).width)
  const firstLineWithChipPx = chip ? measure.measureText(lines[0]).width + chipReserveCss * dpr : 0
  const naturalWidthCss = Math.ceil(Math.max(widestPx, firstLineWithChipPx) / dpr) + paddingCss * 2
  const widthCss = Math.min(maxWidthCss, naturalWidthCss)
  const heightCss = lines.length * lineHeightCss + paddingCss * 2

  const canvas = document.createElement('canvas')
  canvas.width = widthCss * dpr
  canvas.height = heightCss * dpr
  const ctx = canvas.getContext('2d')!
  ctx.font = fontDecl

  ctx.fillStyle = '#' + new Color(bg).getHexString()
  roundedRect(ctx, 0, 0, canvas.width, canvas.height, 6 * dpr)
  ctx.fill()

  ctx.textBaseline = 'middle'
  ctx.lineWidth = 1.5 * dpr
  ctx.strokeStyle = '#' + new Color(bg).getHexString()
  const fillStyle = '#' + new Color(fg).getHexString()
  lines.forEach((ln, i) => {
    const y = padding + lineHeight * (i + 0.5)
    ctx.strokeStyle = '#' + new Color(bg).getHexString()
    ctx.strokeText(ln, padding, y)
    ctx.fillStyle = fillStyle
    ctx.fillText(ln, padding, y)
  })

  if (chip) {
    drawAuthorCircle(ctx, dpr, canvas.width, chipDiameterCss, chip)
  }

  const tex = new Texture(canvas)
  tex.needsUpdate = true
  const mat = new SpriteMaterial({
    map: tex,
    transparent: true,
    sizeAttenuation: true, // shrink with distance like other labels
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new Sprite(mat)
  sprite.position.copy(position)
  // World-units scale: height in metres × number of lines worth, width follows aspect.
  const baseY = LABEL_BASE_HEIGHT_M * (heightCss / 22) // 22 = single-line baseline height
  const baseX = baseY * (widthCss / heightCss)
  sprite.userData.baseScaleX = baseX
  sprite.userData.baseScaleY = baseY
  sprite.scale.set(baseX, baseY, 1)
  sprite.renderOrder = 9999
  sprite.userData.labelSpec = {
    kind: 'wrapped',
    text,
    bg,
    fg,
    chip: chip ?? null,
  }
  sprite.userData.labelAnchor = position.clone()
  return sprite
}
